/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { FormikProps } from 'formik';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, FormControl, FormHelperText, Grid, InputLabel, OutlinedInput } from '@mui/material';

import { OutlinedPasswordInput } from '../../../../elements/OutlinedPasswordInput/OutlinedPasswordInput';
import { Namespaces } from '../../../../i18n';
import { useFormStyles } from '../../../../styles/form';
import { createSignUpPageStyles } from '../../SignUpPage.styles';
import { ResetApiErrorForField, SignUpPageApiValidationErrors, SignUpPageFormValues } from '../../SignUpPage.types';

export type IProps = FormikProps<SignUpPageFormValues> & {
  isFetching: boolean;
  apiErrors: SignUpPageApiValidationErrors;
  resetApiError: ResetApiErrorForField;
  actions: React.ReactNode;
};

export const SignUpUserForm = memo<IProps>(props => {
  const { t } = useTranslation(Namespaces.Login);
  const classes = createSignUpPageStyles();
  const formClasses = useFormStyles();
  const {
    values: { firstName, lastName, email, password, confirmPassword },
    errors,
    touched,
    handleSubmit,
    handleChange,
    setFieldTouched,
    isFetching,
    resetApiError,
    apiErrors,
    actions,
  } = props;

  const change = (name: keyof SignUpPageFormValues, e: any) => {
    e.persist();
    handleChange(e);
    resetApiError(name);
    void setFieldTouched(name, true, false);
  };

  const hasError = (name: keyof SignUpPageFormValues): boolean | undefined => {
    return touched[name] && !!(errors[name] || apiErrors[name]);
  };

  const getError = (name: keyof SignUpPageFormValues): string | undefined => {
    const error = errors[name] || apiErrors[name];
    if (error) {
      return t(`validation.${name}.${error}`);
    }
    return undefined;
  };

  return (
    <form className={classes.form} noValidate onSubmit={handleSubmit}>
      <Box fontWeight="500">
        <FormControl
          variant="outlined"
          className={cx(formClasses.formControl, formClasses.smallOffset)}
          error={hasError('firstName')}
        >
          <div className={classes.formGroup}>
            <InputLabel htmlFor="firstName">{t(`${Namespaces.Entities}:user.firstName.label`)}</InputLabel>
            <OutlinedInput
              id="firstName"
              name="firstName"
              label={t(`${Namespaces.Entities}:user.firstName.label`)}
              error={hasError('firstName')}
              fullWidth
              type="text"
              value={firstName}
              onChange={change.bind(null, 'firstName')}
              disabled={isFetching}
              data-cy="signup-form-firstname-input"
            />
            {hasError('firstName') && (
              <FormHelperText data-cy="signup-form-firstname-error">{getError('firstName')}</FormHelperText>
            )}
          </div>
        </FormControl>
        <FormControl
          variant="outlined"
          className={cx(formClasses.formControl, formClasses.smallOffset)}
          error={hasError('lastName')}
        >
          <div className={classes.formGroup}>
            <InputLabel htmlFor="lastName">{t(`${Namespaces.Entities}:user.lastName.label`)}</InputLabel>
            <OutlinedInput
              id="lastName"
              name="lastName"
              label={t(`${Namespaces.Entities}:user.lastName.label`)}
              error={hasError('lastName')}
              fullWidth
              type="text"
              value={lastName}
              onChange={change.bind(null, 'lastName')}
              disabled={isFetching}
              data-cy="signup-form-lastname-input"
            />
            {hasError('lastName') && (
              <FormHelperText data-cy="signup-form-lastname-error">{getError('lastName')}</FormHelperText>
            )}
          </div>
        </FormControl>
        <FormControl
          variant="outlined"
          className={cx(formClasses.formControl, formClasses.smallOffset)}
          error={hasError('email')}
        >
          <div className={classes.formGroup}>
            <InputLabel htmlFor="email">{t(`${Namespaces.Entities}:user.email.label`)}</InputLabel>
            <OutlinedInput
              id="email"
              name="email"
              label={t(`${Namespaces.Entities}:user.email.label`)}
              error={hasError('email')}
              fullWidth
              type="email"
              value={email}
              onChange={change.bind(null, 'email')}
              disabled={isFetching}
              data-cy="signup-form-email-input"
            />
            {hasError('email') && (
              <FormHelperText data-cy="signup-form-email-error">{getError('email')}</FormHelperText>
            )}
          </div>
        </FormControl>
        <FormControl
          variant="outlined"
          className={cx(formClasses.formControl, formClasses.smallOffset)}
          error={hasError('password')}
        >
          <div className={classes.formGroup}>
            <InputLabel htmlFor="password">{t(`${Namespaces.Entities}:user.password.label`)}</InputLabel>
            <OutlinedPasswordInput
              id="password"
              name="password"
              label={t(`${Namespaces.Entities}:user.password.label`)}
              error={hasError('password')}
              fullWidth
              value={password}
              onChange={change.bind(null, 'password')}
              disabled={isFetching}
              data-cy="signup-form-password-input"
              showTooltip={t(`${Namespaces.Entities}:user.password.field.show`)}
              hideTooltip={t(`${Namespaces.Entities}:user.password.field.hide`)}
            />
            {hasError('password') && (
              <FormHelperText data-cy="signup-form-password-error">{getError('password')}</FormHelperText>
            )}
          </div>
        </FormControl>
        <FormControl
          variant="outlined"
          className={cx(formClasses.formControl, formClasses.smallOffset)}
          error={hasError('confirmPassword')}
        >
          <div className={classes.formGroup}>
            <InputLabel htmlFor="confirmPassword">{t(`${Namespaces.Entities}:user.confirm_password.label`)}</InputLabel>
            <OutlinedPasswordInput
              id="confirmPassword"
              name="confirmPassword"
              label={t(`${Namespaces.Entities}:user.confirm_password.label`)}
              error={hasError('confirmPassword')}
              fullWidth
              value={confirmPassword}
              onChange={change.bind(null, 'confirmPassword')}
              disabled={isFetching}
              data-cy="signup-form-confirm-password-input"
              showTooltip={t(`${Namespaces.Entities}:user.password.field.show`)}
              hideTooltip={t(`${Namespaces.Entities}:user.password.field.hide`)}
            />
            {hasError('confirmPassword') && (
              <FormHelperText data-cy="signup-form-confirm-password-error">
                {getError('confirmPassword')}
              </FormHelperText>
            )}
          </div>
        </FormControl>
        <Grid container className={classes.formFooter}>
          {actions}
        </Grid>
      </Box>
    </form>
  );
});
